/**
 * PDP description tabs.
 *
 * The Krown theme renders description blocks as <toggle-tab> accordions —
 * each block is independent, all start collapsed, and the user has to click
 * each one to expand. The design (design-ref/pdp.jsx) calls for tabs: a
 * single horizontal strip of headings, one panel visible at a time.
 *
 * This script doesn't touch the existing markup or the theme's accordion
 * code. It finds runs of consecutive <toggle-tab> siblings on the product
 * page, wraps each run in a `.pdp-tabs` container, builds a tab strip from
 * the original `.toggle__title` text, and toggles which `<toggle-tab>` is
 * visible via the `hidden` attribute. CSS in pdp-restyle.css then hides the
 * original titles and forces all panels to be expanded — when one is
 * `hidden`, the rest are revealed unstyled like a normal flowing rich-text
 * block.
 *
 * If JS fails to load, the user just sees the existing accordion. No
 * regression risk.
 */

(function () {
  if (window.__pdpTabsInit) return;
  window.__pdpTabsInit = true;

  if (document.body && document.body.classList.contains('template-product')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (document.body.classList.contains('template-product')) init();
    });
  }
  // Theme editor support — re-run when the section reloads.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('toggle-tab')) init();
  });

  function init() {
    // Group runs of consecutive <toggle-tab> siblings.
    const allToggles = Array.from(document.querySelectorAll('toggle-tab'));
    if (allToggles.length < 2) return; // need at least 2 to be useful as tabs

    const groups = [];
    let currentGroup = null;
    allToggles.forEach((tab) => {
      // Skip toggles that have already been wrapped into a tab group.
      if (tab.closest('.pdp-tabs')) return;
      if (currentGroup && tab.previousElementSibling === currentGroup[currentGroup.length - 1]) {
        currentGroup.push(tab);
      } else {
        currentGroup = [tab];
        groups.push(currentGroup);
      }
    });

    groups.forEach(buildTabs);
  }

  function buildTabs(toggles) {
    if (toggles.length < 2) return;

    const parent = toggles[0].parentNode;
    if (!parent) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pdp-tabs';

    const strip = document.createElement('div');
    strip.className = 'pdp-tabs__strip';
    strip.setAttribute('role', 'tablist');

    const buttons = [];
    toggles.forEach((tab, i) => {
      const titleEl = tab.querySelector('.toggle__title');
      const labelText = (titleEl ? titleEl.textContent : `Tab ${i + 1}`).trim();
      const tabId = `pdp-tab-${i}-${Math.random().toString(36).slice(2, 7)}`;
      const panelId = (tab.querySelector('.toggle__content') || {}).id || `pdp-panel-${i}`;

      // Make the original content panel reachable from aria-controls.
      const panel = tab.querySelector('.toggle__content');
      if (panel && !panel.id) panel.id = panelId;
      tab.setAttribute('role', 'tabpanel');
      tab.setAttribute('aria-labelledby', tabId);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.id = tabId;
      btn.className = 'pdp-tabs__tab';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-controls', panelId);
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.textContent = labelText;

      btn.addEventListener('click', () => activate(i));
      btn.addEventListener('keydown', (e) => onKey(e, i));

      buttons.push(btn);
      strip.appendChild(btn);
    });

    // Insert wrapper before the first toggle, then move all toggles inside.
    parent.insertBefore(wrapper, toggles[0]);
    wrapper.appendChild(strip);
    toggles.forEach((tab, i) => {
      wrapper.appendChild(tab);
      tab.hidden = i !== 0;
    });

    function activate(i) {
      buttons.forEach((b, j) => {
        b.setAttribute('aria-selected', j === i ? 'true' : 'false');
        b.setAttribute('tabindex', j === i ? '0' : '-1');
      });
      toggles.forEach((t, j) => {
        t.hidden = j !== i;
      });
      buttons[i].focus();
    }

    function onKey(e, i) {
      const last = buttons.length - 1;
      let next = i;
      if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      else return;
      e.preventDefault();
      activate(next);
    }
  }
})();
